import { all, takeLatest, put, call } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { ATTACHMENT_MAX_SIZES } from '../../consts/consts';
import {
  clearWorklistClaimMessages,
  getWorklistClaimMessages,
  getWorklistClaimRecommendations,
  getWorklistClaimUnreadSince,
  getWorklistClaimReminders,
  setWorklistClaimNewMessageAttachmentsUploadLoading
} from '../worklistClaims/slice';
import { clearInsuredRiskFileContainers, getInsuredRiskFileContainers } from '../fileContainers/slice';
import {
  getFileNameFromApiResponseHeader,
  downloadBlobAsFile,
  getChangedDataFields,
  getFileFromApiResponse
} from '../../helpers/utility';
import { assignGeneralMessage } from '../generalMessages/slice';
import { renderSuccessNotificationWithRedirect } from '../../helpers/notifications/renderSuccessNotificationWithRedirect';
import renderNotification from '../../helpers/notifications/renderNotification';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { onUploadFile } from '../fileUpload/api';
import { uploadFileMessageAttachment } from '../fileUpload/slice';
import { getClaims } from '../claims/slice';
import {
  onGetClaimHistory,
  onGetClaim,
  onGetClaimOrganizationsWithAccess,
  onUpdateClaimStatus,
  onGetClaimPdfSummary,
  onCreateClaim,
  onUpdateClaim,
  onGetRecommendedSettlementPayeeData
} from './api';
import {
  getClaim,
  getClaimSucceeded,
  getClaimFailed,
  getClaimHistory,
  getClaimHistorySucceeded,
  getClaimHistoryFailed,
  getClaimPDFSummaryAndUploadToMessage,
  getClaimPDFSummaryAndUploadToMessageSucceeded,
  getClaimPDFSummaryAndUploadToMessageFailed,
  getClaimOrganizationsWithAccess,
  getClaimOrganizationsWithAccessSucceeded,
  getClaimOrganizationsWithAccessFailed,
  updateClaimStatus,
  updateClaimStatusSucceeded,
  updateClaimStatusFailed,
  getClaimPdfSummary,
  getClaimPdfSummarySucceeded,
  getClaimPdfSummaryFailed,
  createClaim,
  createClaimSucceeded,
  createClaimFailed,
  fetchLatestClaimDataAndUpdateIt,
  fetchLatestClaimDataAndUpdateItSucceeded,
  fetchLatestClaimDataAndUpdateItFailed,
  uploadFileAndUpdateClaim,
  getClaimRecommendedSettlementPayeeData,
  getClaimRecommendedSettlementPayeeDataSucceeded,
  getClaimRecommendedSettlementPayeeDataFailed,
  clearClaim,
  clearClaimHistory,
  clearClaimOrganizationsWithAccess
} from './slice';

export function* getClaimRecommendedSettlementPayeeDataWorker({ payload }) {
  const { claimId } = payload;
  try {
    const response = yield call(onGetRecommendedSettlementPayeeData, claimId);
    yield put(getClaimRecommendedSettlementPayeeDataSucceeded(response.data));
  } catch {
    yield put(getClaimRecommendedSettlementPayeeDataFailed());
  }
}

export function* getClaimWorker({ payload }) {
  const { claimId } = payload;
  try {
    yield all([put(clearClaim()), put(clearInsuredRiskFileContainers()), put(clearWorklistClaimMessages())]);

    const response = yield call(onGetClaim, claimId);
    const prepareActionsArray = (claim) => {
      const _actionsArray = [
        put(getWorklistClaimMessages({ claimId })),
        put(getWorklistClaimRecommendations({ claimId })),
        put(getWorklistClaimUnreadSince({ claimId })),
        put(getWorklistClaimReminders({ claimId })),
        put(getClaimSucceeded(claim)),
        put(getClaimRecommendedSettlementPayeeData({ claimId: claim.id }))
      ];

      const hasInsuredRiskId = !!claim.insured_risk_id;

      if (hasInsuredRiskId) {
        _actionsArray.push(put(getInsuredRiskFileContainers({ insuredRiskId: claim.insured_risk_id })));
      }

      return _actionsArray;
    };

    const actionsArray = prepareActionsArray(response.data);
    yield all(actionsArray);
  } catch {
    yield put(getClaimFailed());
  }
}

// This saga fetches the claim history and enriches the data with UUIDs for each item and its changes.
export function* getClaimHistoryWorker({ payload }) {
  const { claimId } = payload;

  try {
    yield put(clearClaimHistory());
    const response = yield call(onGetClaimHistory, claimId);

    const enrichedData = response.data.map((item) => ({
      ...item,
      _uuid: uuidv4(),
      changes: Array.isArray(item.changes)
        ? item.changes.map((change) => ({
            ...change,
            _uuid: uuidv4()
          }))
        : item.changes
    }));

    yield put(getClaimHistorySucceeded(enrichedData));
  } catch {
    yield put(getClaimHistoryFailed());
  }
}

export function* getClaimOrganizationsWithAccessWorker({ payload }) {
  const { claimId } = payload;

  try {
    yield put(clearClaimOrganizationsWithAccess());
    const response = yield call(onGetClaimOrganizationsWithAccess, claimId);
    yield put(getClaimOrganizationsWithAccessSucceeded(response.data));
  } catch {
    yield put(getClaimOrganizationsWithAccessFailed());
  }
}

export function* updateClaimStatusWorker({ payload }) {
  const { claimId, status, callback } = payload;

  try {
    const response = yield call(onUpdateClaimStatus, claimId, status);

    const updatedClaim = response.data;
    yield all([
      put(updateClaimStatusSucceeded(updatedClaim)),
      put(getClaimHistory({ claimId })),
      put(getWorklistClaimRecommendations({ claimId }))
    ]);
    callback?.();
  } catch (error) {
    yield put(updateClaimStatusFailed(error));
  }
}

export function* getClaimPdfSummaryWorker({ payload }) {
  const { claimId, callback } = payload;

  try {
    const response = yield call(onGetClaimPdfSummary, claimId);
    yield put(getClaimPdfSummarySucceeded());

    const fileName = getFileNameFromApiResponseHeader(response.headers, '.pdf');
    const blob = new Blob([response.data], { type: 'application/pdf' });

    downloadBlobAsFile(blob, fileName);
    callback?.();
  } catch {
    yield put(getClaimPdfSummaryFailed());
  }
}

// function to get the claim pdf summary and upload it to the message attachments
export function* getClaimPDFSummaryAndUploadToMessageWorker({ payload }) {
  const { claimId, callback } = payload;
  try {
    const response = yield call(onGetClaimPdfSummary, claimId);
    const pdfClaimFile = getFileFromApiResponse(response);

    yield put(getClaimPDFSummaryAndUploadToMessageSucceeded());

    const isFileBiggerThanAllowed = pdfClaimFile.size > ATTACHMENT_MAX_SIZES.default.byte;

    if (isFileBiggerThanAllowed) {
      yield put(setWorklistClaimNewMessageAttachmentsUploadLoading(false));

      renderNotification({
        type: 'error',
        message: NOTIFICATION_MESSAGES.claim_pdf_report_too_big_for_message_error,
        duration: 5
      });
    } else {
      yield put(
        uploadFileMessageAttachment({ file: pdfClaimFile, attachmentId: claimId, attachmentType: 'claimReport' })
      );
    }

    if (callback && typeof callback === 'function') {
      callback();
    }
  } catch {
    yield put(getClaimPDFSummaryAndUploadToMessageFailed());
  }
}

export function* createClaimWorker({ payload }) {
  const { claimData, messageToBeAssigned, callback, navigate, queryParams } = payload;

  try {
    const response = yield call(onCreateClaim, claimData);
    if (messageToBeAssigned) {
      yield put(
        assignGeneralMessage({
          message: messageToBeAssigned,
          entityType: 'claim',
          entityId: response.data.id
        })
      );
    }
    yield put(createClaimSucceeded(response.data));

    if (queryParams) {
      yield put(getClaims({ params: queryParams }));
    }

    callback?.();

    const notificationMessage = NOTIFICATION_MESSAGES.claim_create_success;
    renderSuccessNotificationWithRedirect(
      notificationMessage,
      'Schaden ansehen',
      `/claim/${response.data.id}/main`,
      navigate
    );
  } catch {
    yield put(createClaimFailed());
  }
}

export function* fetchLatestClaimDataAndUpdateItWorker({ payload }) {
  const { initialLocalClaimData, updatedLocalClaimData, callback } = payload;

  try {
    const getClaimResponse = yield call(onGetClaim, initialLocalClaimData.id);
    const latestClaimDataFromDatabase = getClaimResponse.data;

    const changedDataFields = getChangedDataFields(initialLocalClaimData, updatedLocalClaimData);

    // In the following we update the claim data from the database with the changes done by the user in the interface
    // Only the changed fields are updated not the entire claim so that we reduce the possible scenarios of overriding changes done by other users
    Object.keys(changedDataFields).forEach((key) => {
      if (Array.isArray(latestClaimDataFromDatabase[key])) {
        const valuesArray = latestClaimDataFromDatabase[key];
        const { itemsToBeAdded, itemsToBeRemoved, itemsToBeUpdated } = changedDataFields[key];

        itemsToBeAdded.forEach((item) => {
          valuesArray.push(item);
        });

        itemsToBeRemoved.forEach((item) => {
          const index = valuesArray.findIndex((value) => value.id === item.id);
          if (index !== -1) {
            valuesArray.splice(index, 1);
          }
        });

        itemsToBeUpdated.forEach((item) => {
          const index = valuesArray.findIndex((value) => value.id === item.id);
          if (index !== -1) {
            valuesArray[index] = { ...valuesArray[index], ...item };
          }
        });

        latestClaimDataFromDatabase[key] = valuesArray;
      } else {
        latestClaimDataFromDatabase[key] = changedDataFields[key];
      }
    });

    const response = yield call(onUpdateClaim, latestClaimDataFromDatabase);

    yield all([put(clearInsuredRiskFileContainers()), put(clearClaimOrganizationsWithAccess())]);
    const prepareActionsArray = (claim) => {
      const _actionsArray = [
        put(fetchLatestClaimDataAndUpdateItSucceeded(claim)),
        put(getClaimHistory({ claimId: claim.id })),
        put(getWorklistClaimRecommendations({ claimId: claim.id })),
        put(getClaimOrganizationsWithAccess({ claimId: claim.id }))
      ];

      const hasInsuredRiskId = !!claim.insured_risk_id;
      if (hasInsuredRiskId) {
        _actionsArray.push(put(getInsuredRiskFileContainers({ insuredRiskId: claim.insured_risk_id })));
      }

      return _actionsArray;
    };

    const actionsArray = prepareActionsArray(response.data);
    yield all(actionsArray);

    if (callback && typeof callback === 'function') {
      callback();
    }
  } catch {
    yield put(fetchLatestClaimDataAndUpdateItFailed());
  }
}

export function* uploadFileAndUpdateClaimWorker({ payload }) {
  const { file, file_container_id, claim, callback, errorCallback } = payload;

  try {
    // upload file
    const upload = yield call(onUploadFile, file, file_container_id);
    if (!upload || !upload.data) {
      if (typeof errorCallback === 'function') errorCallback();
      return;
    }

    // add new blob
    const updatedPayload = { ...claim, blobs: [...claim.blobs, upload.data] };

    // callback
    const afterUpdate = () => {
      if (typeof callback === 'function') callback(upload.data);
    };

    yield put(
      fetchLatestClaimDataAndUpdateIt({
        initialLocalClaimData: claim,
        updatedLocalClaimData: updatedPayload,
        callback: afterUpdate
      })
    );
  } catch (error) {
    if (typeof errorCallback === 'function') errorCallback(error);
  }
}

export default function* claimSaga() {
  yield all([
    takeLatest(getClaim.type, getClaimWorker),
    takeLatest(getClaimRecommendedSettlementPayeeData.type, getClaimRecommendedSettlementPayeeDataWorker),
    takeLatest(getClaimHistory.type, getClaimHistoryWorker),
    takeLatest(getClaimOrganizationsWithAccess.type, getClaimOrganizationsWithAccessWorker),
    takeLatest(updateClaimStatus.type, updateClaimStatusWorker),
    takeLatest(getClaimPdfSummary.type, getClaimPdfSummaryWorker),
    takeLatest(getClaimPDFSummaryAndUploadToMessage.type, getClaimPDFSummaryAndUploadToMessageWorker),
    takeLatest(createClaim.type, createClaimWorker),
    takeLatest(fetchLatestClaimDataAndUpdateIt.type, fetchLatestClaimDataAndUpdateItWorker),
    takeLatest(uploadFileAndUpdateClaim.type, uploadFileAndUpdateClaimWorker)
  ]);
}
